import { useState } from "react";
import { useQueryClient } from "react-query";
import SyntaxHighlighter from "react-syntax-highlighter";
import { a11yDark } from "react-syntax-highlighter/dist/esm/styles/hljs";

import {
  DefaultButton,
  Dropdown,
  Heading,
  IconButton,
  IDropdownOption,
  IModalProps,
  Modal,
  Stack,
  useTheme
} from "@bps/fluent-ui";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import { FieldDeviceConfiguration } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import {
  FieldOpsCacheKeys,
  useViewDeploymentRingCachedBlock
} from "@libs/api/gateways/field/field-ops-gateway.hooks";

enum FieldConfigurationCacheVersion {
  V3_0 = "v3",
  V2_0 = "v2"
}

interface ViewConfigurationModalProps extends IModalProps {
  show: boolean;
  ringId: string;
  onDismiss: () => void;
}

export const ViewConfigurationModal: React.FC<ViewConfigurationModalProps> = ({
  ringId,
  show,
  onDismiss,
  ...props
}) => {
  const [configurationVersion, setConfigurationVersion] = useState<string>(
    FieldConfigurationCacheVersion.V3_0
  );

  const deploymentRingCachedBlockQuery = useViewDeploymentRingCachedBlock(
    ringId,
    configurationVersion
  );

  const queryClient = useQueryClient();

  const copyToClipboard = async () => {
    const configurationResult = queryClient.getQueryData([
      FieldOpsCacheKeys.DeploymentRingConfigCache,
      ringId,
      configurationVersion
    ]);

    await navigator.clipboard.writeText(
      JSON.stringify(configurationResult, null, 2)
    );
  };

  const configurationVersionOptions: IDropdownOption[] = Object.keys(
    FieldConfigurationCacheVersion
  ).map(key => ({
    key: FieldConfigurationCacheVersion[key],
    text: `Field Configuration ${FieldConfigurationCacheVersion[key]}`
  }));

  const theme = useTheme();
  return (
    <Modal
      isOpen={show}
      {...props}
      styles={{ scrollableContent: { maxHeight: "none" } }}
    >
      <Stack
        verticalFill
        styles={{
          root: { padding: theme.spacing.l1, maxHeight: 800 }
        }}
        tokens={{ childrenGap: theme.spacing.s1 }}
      >
        <Stack horizontal horizontalAlign="space-between">
          <Heading variant="modal-heading">
            Deployment Ring Configuration Cache
          </Heading>
          <IconButton onClick={onDismiss} iconProps={{ iconName: "Cancel" }} />
        </Stack>

        <Stack
          verticalFill
          styles={{
            root: { maxHeight: 600, overflow: "auto", maxWidth: 1200 }
          }}
        >
          <QueryStateIndicator<FieldDeviceConfiguration>
            {...deploymentRingCachedBlockQuery}
          >
            {json => (
              <SyntaxHighlighter
                language="json"
                // eslint-disable-next-line react/forbid-component-props
                style={a11yDark}
                wrapLongLines
                wrapLines
                customStyle={{
                  wordBreak: "break-word"
                }}
              >
                {JSON.stringify(json, null, 2)}
              </SyntaxHighlighter>
            )}
          </QueryStateIndicator>
        </Stack>

        <Stack
          horizontal
          tokens={{ childrenGap: theme.spacing.s1 }}
          horizontalAlign="end"
        >
          <Dropdown
            name="configuration-version-dropdown"
            onChange={(_e, options) =>
              setConfigurationVersion(String(options.option?.key))
            }
            options={configurationVersionOptions}
            selectedKey={configurationVersion}
            defaultSelectedKey={FieldConfigurationCacheVersion.V3_0}
            withNoEmptyOption
          />
          <DefaultButton onClick={copyToClipboard}>
            Copy to Clipboard
          </DefaultButton>
          <DefaultButton onClick={onDismiss}>Cancel</DefaultButton>
        </Stack>
      </Stack>
    </Modal>
  );
};
